import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { DealListItem, RegistrationStatus } from "../../../Models/ambassador";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import {
  RegistrationStatusChip,
  SalesStatusChip,
} from "../Common/AmbassadorUi";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import { formatDate } from "../Common/format";

/** The ambassador's deals; drafts open the form, everything else the details. */
const MyDealsTable: React.FC<{ deals: DealListItem[]; compact?: boolean }> = ({
  deals,
  compact,
}) => {
  const { t, i18n } = useTranslation("ambassador");
  const navigate = useNavigate();
  const { primary, accentText } = useDashboardColors();

  const open = (deal: DealListItem) =>
    navigate(
      deal.registration_status === RegistrationStatus.DRAFT
        ? ambassadorPaths.editDraft(deal.id)
        : ambassadorPaths.myDeal(deal.id),
    );

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {!compact && (
              <TableCell>{t("myDeals.columns.dealNumber")}</TableCell>
            )}
            <TableCell>{t("myDeals.columns.customer")}</TableCell>
            {compact && (
              <TableCell>{t("myDeals.columns.dealNumber")}</TableCell>
            )}
            {!compact && (
              <TableCell>{t("myDeals.columns.lastUpdate")}</TableCell>
            )}
            <TableCell>{t("myDeals.columns.registration")}</TableCell>
            {!compact && <TableCell>{t("myDeals.columns.sales")}</TableCell>}
            {compact ? (
              <TableCell>{t("myDeals.columns.update")}</TableCell>
            ) : (
              <TableCell />
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {deals.map((deal) => {
            const number = deal.deal_number ?? t("myDeals.noNumber");
            return (
              <TableRow
                key={deal.id}
                hover
                onClick={() => open(deal)}
                sx={{ cursor: "pointer" }}
              >
                {!compact && (
                  <TableCell
                    sx={{ color: color.gray500, whiteSpace: "nowrap" }}
                  >
                    {number}
                  </TableCell>
                )}
                <TableCell sx={{ minWidth: 180 }}>
                  <Typography
                    sx={{ fontWeight: 700, color: primary, fontSize: 14 }}
                  >
                    {deal.company_name || t("myDeals.noCompany")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                    {deal.title || t("myDeals.noTitle")}
                  </Typography>
                </TableCell>
                {compact && (
                  <TableCell
                    sx={{ color: color.gray500, whiteSpace: "nowrap" }}
                  >
                    {number}
                  </TableCell>
                )}
                {!compact && (
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDate(deal.last_update_at, i18n.language)}
                  </TableCell>
                )}
                <TableCell>
                  <RegistrationStatusChip status={deal.registration_status} />
                </TableCell>
                {!compact && (
                  <TableCell>
                    <SalesStatusChip status={deal.sales_status} />
                  </TableCell>
                )}
                {compact ? (
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDate(deal.last_update_at, i18n.language)}
                  </TableCell>
                ) : (
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: accentText,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {deal.registration_status === RegistrationStatus.DRAFT
                        ? t("myDeals.continue")
                        : t("common.details")}
                      <ArrowForwardRounded sx={{ fontSize: 16 }} />
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default MyDealsTable;
